/**
 * /dashboard/me/surveys/$id — take one assigned survey (G16).
 *
 * Backend contract (GET /api/v1/me/surveys/{id}):
 *   - 404 not_found when the survey doesn't exist OR isn't active (HR
 *     deliberately serves the same code so the existence of draft /
 *     closed surveys isn't oraclable to non-HR).
 *   - 409 already_responded when the caller has already submitted —
 *     route back to the list with a toast (and don't surface a retry).
 *
 * The form itself (validation + render per question_type + submit)
 * lives in TakeSurveyForm.
 */

import { createFileRoute, Link, useNavigate , useParams} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { useMySurvey } from "@/hooks/use-surveys";
import { PageHeader } from "@/components/shared/page-header";
import { TakeSurveyForm } from "@/components/surveys/take-survey-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/surveys/$id")({
  component: TakeSurveyRoute,
});

const TAKE_SK_KEYS = ["sk-q1", "sk-q2", "sk-q3"] as const;

function TakeSurveyRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const surveyId = Number(id);
  const q = useMySurvey(surveyId);

  // Terminal-state errors (already-responded, not-found) should route
  // the user back to the list rather than offer a retry button. The
  // backend returns these statuses deliberately; a retry would 404
  // again.
  useEffect(() => {
    if (!q.isError) return;
    const apiErr = toApiError(q.error);
    if (apiErr.error === "already_responded") {
      toast.message("You have already responded to this survey.");
      void navigate({ to: "/app/$tenantSlug/me/surveys", params: { tenantSlug }, replace: true });
    } else if (apiErr.error === "not_found") {
      toast.message("This survey is no longer available.");
      void navigate({ to: "/app/$tenantSlug/me/surveys", params: { tenantSlug }, replace: true });
    }
  }, [q.isError, q.error, navigate]);

  return (
    <>
      <PageHeader
        title={q.data?.survey.title ?? "Survey"}
        description={
          q.data?.survey
            ? `${q.data.questions.length} question${q.data.questions.length === 1 ? "" : "s"} — your responses go to HR.`
            : undefined
        }
        actions={
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/me/surveys" params={{ tenantSlug }}>
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      {q.isLoading && (
        <div className="space-y-4">
          {TAKE_SK_KEYS.map((k) => (
            <Card key={k}>
              <CardHeader>
                <Skeleton className="h-5 w-2/3" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load survey</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.data && (
        <TakeSurveyForm survey={q.data.survey} questions={q.data.questions} />
      )}
    </>
  );
}
